import { useEffect, useState } from "react";
import { BellRing, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { currentSubscription, disablePush, enablePush, pushSupport } from "../../lib/pwa";

const SUPPORT_TEXT = {
  "ios-install": "Sur iPhone et iPad (iOS 16.4 ou plus récent), installez d'abord VOXA AFRIKA sur l'écran d'accueil, puis ouvrez-la depuis l'icône pour activer les notifications push.",
  unsupported: "Ce navigateur ne prend pas en charge les notifications push. Vous recevez toujours les notifications dans l'application et par email.",
  denied: "Les notifications sont bloquées pour ce site dans les réglages du navigateur. Autorisez-les dans les paramètres du site pour les réactiver.",
};

const DevicePush = ({ enabled, onChange }) => {
  const support = pushSupport();
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setActive(!!s)).catch(() => setActive(false)); }, []);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(e.response ? formatApiError(e) : e.message); } finally { setBusy(false); }
  };
  if (!enabled) {
    return <p className="text-sm text-slate-500" data-testid="push-server-disabled">Les notifications push ne sont pas activées sur ce serveur.</p>;
  }
  if (support !== "ok") {
    return <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="push-unsupported">{SUPPORT_TEXT[support]}</p>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="push-device">
      <span className="text-sm" data-testid="push-device-status">{active ? "Activées sur cet appareil." : "Désactivées sur cet appareil."}</span>
      {!active && <Button className="h-11 rounded-full" disabled={busy} onClick={() => run(async () => { await enablePush(); setActive(true); }, "Notifications push activées")} data-testid="push-enable">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BellRing className="mr-1.5 h-4 w-4" />}Activer sur cet appareil</Button>}
      {active && <>
        <Button variant="outline" className="h-11 rounded-full" disabled={busy} onClick={() => run(() => api.post("/push/test"), "Notification de test envoyée")} data-testid="push-test"><Send className="mr-1.5 h-4 w-4" />Envoyer un test</Button>
        <Button variant="ghost" className="h-11 rounded-full" disabled={busy} onClick={() => run(async () => { await disablePush(); setActive(false); }, "Notifications push désactivées sur cet appareil")} data-testid="push-disable">Désactiver</Button></>}
    </div>
  );
};

const Matrix = ({ data, onSaved }) => {
  const [prefs, setPrefs] = useState(data.prefs);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPrefs(data.prefs); }, [data]);
  const set = (k, ch, v) => setPrefs({ ...prefs, [k]: { ...prefs[k], [ch]: v } });
  const save = async () => {
    setBusy(true);
    try { await api.put("/me/notification-preferences", { prefs: Object.fromEntries(Object.entries(prefs).map(([k, v]) => [k, { email: v.email, push: v.push }])) }); toast.success("Préférences enregistrées"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="notif-prefs-table">
        <thead><tr className="text-left text-xs text-slate-500"><th className="py-2">Événement</th><th className="px-2">Application</th><th className="px-2">Email</th><th className="px-2">Push</th></tr></thead>
        <tbody>{data.categories.map((c) => (
          <tr key={c.key} className="border-t border-slate-100"><td className="py-3 pr-2">{c.label}</td>
            <td className="px-2 text-xs text-slate-500">Toujours</td>
            <td className="px-2"><Switch checked={prefs[c.key].email} onCheckedChange={(v) => set(c.key, "email", v)} aria-label={`Email : ${c.label}`} data-testid={`notif-${c.key}-email`} /></td>
            <td className="px-2"><Switch checked={prefs[c.key].push} onCheckedChange={(v) => set(c.key, "push", v)} aria-label={`Push : ${c.label}`} data-testid={`notif-${c.key}-push`} /></td></tr>
        ))}</tbody>
      </table></div>
      <p className="mt-2 text-xs text-slate-500">Les emails de sécurité (connexion, vérification, mot de passe) sont toujours envoyés. Le contenu des dossiers n'apparaît jamais dans une notification push.</p>
      <Button onClick={save} disabled={busy} className="mt-4 h-11 rounded-full" data-testid="notif-prefs-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Enregistrer</Button>
    </div>
  );
};

const MarketingConsent = () => {
  const { data, reload } = useApi("/me/marketing-consent");
  const toggle = async (v) => {
    try { await api.put("/me/marketing-consent", { subscribed: v }); toast.success(v ? "Inscription enregistrée" : "Désinscription prise en compte immédiatement"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <label className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3 text-sm" data-testid="marketing-consent">
      <span><b>Actualités et offres VOXA AFRIKA</b><span className="block text-xs text-slate-500">Newsletters et annonces, uniquement avec votre accord. Désinscription possible à tout moment.</span></span>
      <Switch checked={!!data?.subscribed} onCheckedChange={toggle} data-testid="marketing-consent-switch" />
    </label>
  );
};

export default function NotificationSettings() {
  const { data, reload } = useApi("/me/notification-preferences");
  return (
    <div className="card-surface space-y-5 p-6" data-testid="notification-settings">
      <div><p className="font-display font-semibold">Notifications</p><p className="text-sm text-slate-500">Choisissez comment être prévenu. L'autorisation push n'est demandée que lorsque vous l'activez.</p></div>
      {data && <DevicePush enabled={data.push_enabled} onChange={reload} />}
      {data && <Matrix data={data} onSaved={reload} />}
      <MarketingConsent />
    </div>
  );
}
